import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  HostBinding,
  inject,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TimePipe } from '@app/pipes/time.pipe';
import { getEnhancedAbsenceById } from '@app/reducers/orm/absence/absence.service';
import { Store } from '@ngrx/store';
import { AppState } from '@reducers/interfaces';
import { AbsenceModel, AbsenceStatus, EnhancedAbsenceModel } from '@reducers/orm/absence/absence.model';
import { IconComponent } from '@sb/ui/icon';
import { clsx } from 'clsx';
import { take } from 'rxjs/operators';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'absence-item-content',
  templateUrl: './absence-item-content.component.html',
  imports: [IconComponent, TimePipe],
  host: {
    '[class.text-on-color-light]': 'usesLightContrast()',
    '[class.text-on-color-dark]': '!usesLightContrast() && !isPending',
    '[class.text-default]': 'isPending',
  },
})
export class AbsenceItemContentComponent implements OnChanges {
  private store = inject<Store<AppState>>(Store);
  private destroyRef = inject(DestroyRef);

  @HostBinding('class')
  class = clsx([
    'flex',
    'h-8.5',
    'max-w-full',
    'items-center',
    'gap-1',
    'rounded-lg',
    'border-2',
    'border-solid',
    'px-1',
    'py-0.5',
    'shadow-xs',
  ]);

  usesLightContrast(): boolean {
    return !!this.absence.absenteeOption?.color_is_dark && !this.isPending;
  }

  @HostBinding('class.justify-center')
  @Input()
  compactView: boolean;

  @HostBinding('style.backgroundColor')
  get backgroundColor() {
    return this.isPending ? 'var(--colors-background-surface-page)' : this.absence.absenteeOption?.color;
  }

  @HostBinding('style.borderColor')
  get borderColor() {
    return this.absence.absenteeOption?.color;
  }

  @Input({ required: true })
  set absence(absence: AbsenceModel | EnhancedAbsenceModel) {
    if (absence.absenteeOption) {
      this._absence = {
        ...(absence as EnhancedAbsenceModel),
      };
    } else {
      this.setEnhancedAbsence();
    }
  }
  get absence(): AbsenceModel | EnhancedAbsenceModel {
    return this._absence;
  }

  @Input({ required: true })
  date: string;

  @Input()
  mode: 'team' | 'employee' = 'employee';

  isPending: boolean;

  private _absence: EnhancedAbsenceModel;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['absence']) {
      this.isPending = this.absence.status === AbsenceStatus.PENDING;
    }
  }

  private setEnhancedAbsence() {
    void this.store
      .select(getEnhancedAbsenceById(this.absence.id))
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((absence: EnhancedAbsenceModel) => {
        this._absence = {
          ...absence,
        };
      });
  }
}
